import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppDateInput from '@/app/admin/components/AppDateInput';
import { Button } from '@/ui/compat/antd';
import { SearchOutlined, ReloadOutlined } from '@/ui/compat/icons';

const initialState = {
  status: '',
  q: '',
  matricule: '',
  voucherStatus: '',
  checkInFrom: '',
  checkInTo: '',
  bookingFrom: '',
  bookingTo: '',
};

const AmicaleReservationsFilters = ({ value, onChange, onReset, productType = 'hotel' }) => {
  const { t } = useTranslation();
  const isHotel = productType === 'hotel';
  const [local, setLocal] = useState(value || initialState);

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('amicales.filters.statusAll') },
      { value: 'pending', label: t('amicales.filters.statusPending') },
      { value: 'validated', label: t('amicales.filters.statusValidated') },
      { value: 'rejected', label: t('amicales.filters.statusRejected') },
    ],
    [t]
  );

  const voucherStatusOptions = useMemo(
    () => [
      { value: '', label: t('amicales.filters.voucherStatusAll') },
      { value: 'disponible', label: t('amicales.filters.voucherAvailable') },
      { value: 'sur demande', label: t('amicales.filters.voucherOnRequest') },
      { value: 'cloturé', label: t('amicales.filters.voucherClosed') },
      { value: 'annulé', label: t('amicales.filters.voucherCancelled') },
    ],
    [t]
  );

  useEffect(() => {
    if (value) setLocal(value);
  }, [value]);

  const setField = (field, val) => setLocal((prev) => ({ ...prev, [field]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onChange?.(local);
  };

  const handleReset = () => {
    setLocal(initialState);
    onReset?.();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className='bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4'
    >
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3'>
        <div>
          <label className='block text-xs font-medium text-gray-600 mb-1'>
            {t('amicales.filters.status')}
          </label>
          <select
            value={local.status}
            onChange={(e) => setField('status', e.target.value)}
            className='w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className='block text-xs font-medium text-gray-600 mb-1'>
            {isHotel ? t('amicales.filters.searchLabelHotel') : t('amicales.filters.searchLabelOther')}
          </label>
          <input
            type='text'
            value={local.q}
            onChange={(e) => setField('q', e.target.value)}
            className='w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            placeholder={t('amicales.filters.keywordPlaceholder')}
          />
        </div>

        <div>
          <label className='block text-xs font-medium text-gray-600 mb-1'>
            {t('amicales.filters.matricule')}
          </label>
          <input
            type='text'
            value={local.matricule}
            onChange={(e) => setField('matricule', e.target.value)}
            className='w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            placeholder={t('amicales.filters.matriculePlaceholder')}
          />
        </div>

        {isHotel ? (
          <div>
            <label className='block text-xs font-medium text-gray-600 mb-1'>
              {t('amicales.filters.voucherStatus')}
            </label>
            <select
              value={local.voucherStatus}
              onChange={(e) => setField('voucherStatus', e.target.value)}
              className='w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              {voucherStatusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div>
          <label className='block text-xs font-medium text-gray-600 mb-1'>
            {isHotel ? t('amicales.filters.checkInFrom') : t('amicales.filters.accessFrom')}
          </label>
          <AppDateInput
            value={local.checkInFrom}
            onChange={(value) => setField('checkInFrom', value)}
            compact
          />
        </div>

        <div>
          <label className='block text-xs font-medium text-gray-600 mb-1'>
            {isHotel ? t('amicales.filters.checkInTo') : t('amicales.filters.accessTo')}
          </label>
          <AppDateInput
            value={local.checkInTo}
            onChange={(value) => setField('checkInTo', value)}
            compact
          />
        </div>

        <div>
          <label className='block text-xs font-medium text-gray-600 mb-1'>
            {t('amicales.filters.bookingFrom')}
          </label>
          <AppDateInput
            value={local.bookingFrom}
            onChange={(value) => setField('bookingFrom', value)}
            compact
          />
        </div>

        <div>
          <label className='block text-xs font-medium text-gray-600 mb-1'>
            {t('amicales.filters.bookingTo')}
          </label>
          <AppDateInput
            value={local.bookingTo}
            onChange={(value) => setField('bookingTo', value)}
            compact
          />
        </div>
      </div>

      <div className='flex justify-end gap-2 mt-3'>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleReset}
          htmlType='button'
        >
          {t('amicales.filters.reset')}
        </Button>
        <Button
          icon={<SearchOutlined />}
          type='primary'
          htmlType='submit'
          style={{ background: 'var(--color-primary-800)', borderColor: 'var(--color-primary-800)' }}
        >
          {t('amicales.filters.apply')}
        </Button>
      </div>
    </form>
  );
};

export default AmicaleReservationsFilters;
export { initialState as INITIAL_FILTERS };
